import {
  forwardRef,
  type DragEventHandler,
  type HTMLAttributes,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';

import { cn } from '../../lib/utils.js';
import { ShellIconArrowDown, ShellIconArrowUp } from '../patterns/app-shell/shell-stroke-icon.js';

export type TableWrapProps = HTMLAttributes<HTMLDivElement> & {
  scroll?: boolean;
};

export function TableWrap({ className, scroll = false, ...props }: TableWrapProps) {
  return <div className={cn('table-wrap', scroll && 'table-wrap--scroll', className)} {...props} />;
}

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  zebra?: boolean;
  ultra?: boolean;
  layout?: 'fixed' | 'auto';
}

export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  { className, zebra = false, ultra = false, layout = 'fixed', ...props },
  ref,
) {
  return (
    <table
      ref={ref}
      className={cn(
        'tbl',
        zebra && 'tbl--zebra',
        ultra && 'tbl--ultra',
        layout === 'auto' && 'tbl--auto',
        className,
      )}
      {...props}
    />
  );
});

export type TableHeadProps = HTMLAttributes<HTMLTableSectionElement>;
export type TableBodyProps = HTMLAttributes<HTMLTableSectionElement>;
export type TableRowProps = HTMLAttributes<HTMLTableRowElement> & { selected?: boolean };
export type TableSortDirection = 'asc' | 'desc';
export type TableHeaderProps = ThHTMLAttributes<HTMLTableCellElement> & {
  sortDirection?: TableSortDirection | false;
  onSort?: () => void;
  columnKey?: string;
  onColumnReorder?: (fromKey: string, toKey: string) => void;
};
export type TableCellProps = TdHTMLAttributes<HTMLTableCellElement> & {
  numeric?: boolean;
  mono?: boolean;
};

export function TableHead({ className, ...props }: TableHeadProps) {
  return <thead className={className} {...props} />;
}

export function TableBody({ className, ...props }: TableBodyProps) {
  return <tbody className={className} {...props} />;
}

export function TableRow({ className, selected = false, ...props }: TableRowProps) {
  return <tr className={cn(selected && 'is-selected', className)} {...props} />;
}

let activeTableColumnDragKey: string | null = null;
let suppressSortAfterColumnDrag = false;

export function TableHeader({
  className,
  sortDirection,
  onSort,
  columnKey,
  onColumnReorder,
  children,
  ...props
}: TableHeaderProps) {
  const sortable = onSort !== undefined;
  const reorderable = columnKey !== undefined && onColumnReorder !== undefined;
  const ariaSort =
    sortDirection === 'asc'
      ? 'ascending'
      : sortDirection === 'desc'
        ? 'descending'
        : sortable
          ? 'none'
          : undefined;

  const handleDragStart = () => {
    if (columnKey === undefined) {
      return;
    }

    activeTableColumnDragKey = columnKey;
    suppressSortAfterColumnDrag = true;
  };

  const handleDragOver: DragEventHandler<HTMLTableCellElement> = (event) => {
    if (!reorderable || activeTableColumnDragKey === null) {
      return;
    }

    event.preventDefault();
  };

  const handleDrop: DragEventHandler<HTMLTableCellElement> = (event) => {
    event.preventDefault();
    if (!reorderable) {
      return;
    }

    const fromKey = activeTableColumnDragKey;
    if (fromKey === null || fromKey === columnKey) {
      return;
    }

    onColumnReorder(fromKey, columnKey);
  };

  const handleDragEnd = () => {
    activeTableColumnDragKey = null;
    window.setTimeout(() => {
      suppressSortAfterColumnDrag = false;
    }, 0);
  };

  const handleSortClick = () => {
    if (suppressSortAfterColumnDrag) {
      suppressSortAfterColumnDrag = false;
      return;
    }

    onSort?.();
  };

  if (!sortable) {
    return (
      <th
        className={cn(reorderable && 'tbl-th--reorderable', className)}
        draggable={reorderable}
        onDragStart={reorderable ? handleDragStart : undefined}
        onDragOver={reorderable ? handleDragOver : undefined}
        onDrop={reorderable ? handleDrop : undefined}
        onDragEnd={reorderable ? handleDragEnd : undefined}
        {...props}
      >
        {children}
      </th>
    );
  }

  return (
    <th
      className={cn('tbl-th--sortable', reorderable && 'tbl-th--reorderable', className)}
      aria-sort={ariaSort}
      onDragOver={reorderable ? handleDragOver : undefined}
      onDrop={reorderable ? handleDrop : undefined}
      onDragEnd={reorderable ? handleDragEnd : undefined}
      {...props}
    >
      <button
        type="button"
        className="tbl-th-sort"
        draggable={reorderable}
        onDragStart={reorderable ? handleDragStart : undefined}
        onClick={handleSortClick}
      >
        <span className="tbl-th-sort__label">{children}</span>
        {sortDirection === 'desc' ? (
          <ShellIconArrowDown size={10} className="tbl-th-sort__icon" aria-hidden />
        ) : (
          <ShellIconArrowUp size={10} className="tbl-th-sort__icon" aria-hidden />
        )}
      </button>
    </th>
  );
}

export function TableCell({ className, numeric = false, mono = false, ...props }: TableCellProps) {
  return <td className={cn(numeric && 'num', mono && 'mono', className)} {...props} />;
}
